import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, Banknote } from "lucide-react";
import { fileUrl, SAR } from "@/lib/api";

export const Stars = ({ value = 0, size = 14 }) => {
  const v = Math.round(value * 2) / 2;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24"
          fill={i <= v ? "#F59E0B" : "none"} stroke="#F59E0B" strokeWidth="1.5">
          <path d="M12 2l3 6.5 7 .9-5 4.8 1.3 7L12 18l-6.6 3.2 1.3-7-5-4.8 7-.9z" />
        </svg>
      ))}
    </span>
  );
};

export const PartCard = ({ part }) => {
  const img = part.images && part.images[0] ? fileUrl(part.images[0]) : "";
  return (
    <Link to={`/part/${part.id}`} data-testid={`part-card-${part.id}`} className="ts-card group overflow-hidden flex flex-col animate-fade-up">
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--bg-secondary)]">
        {img ? (
          <img src={img} alt={part.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
        ) : (
          <div className="flex h-full items-center justify-center text-[var(--text-muted)] text-sm">لا توجد صورة</div>
        )}
        {part.featured && (
          <span className="absolute top-3 right-3 rounded-lg bg-[var(--amber)] px-2 py-1 text-[10px] font-black text-[#0B0F17]">مميزة</span>
        )}
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-lg bg-[var(--emerald)] px-2 py-1 text-[10px] font-bold text-white">
          <Banknote size={12} /> دفع عند الاستلام
        </span>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-bold text-white text-sm line-clamp-2 leading-snug min-h-[2.5rem]">{part.name}</h3>
        <p className="text-xs text-[var(--text-muted)]">{part.make} {part.model} · {part.condition}</p>
        <div className="mt-auto flex items-end justify-between pt-2">
          <div>
            <span className="font-head text-xl font-black text-[var(--amber)]">{part.price.toLocaleString("ar")}</span>
            <span className="text-xs text-[var(--text-muted)] mr-1">{SAR}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-[var(--text-muted)] border-t pt-2" style={{ borderColor: "var(--border-c)" }}>
          <ShieldCheck size={13} className="text-[var(--emerald)]" />
          <span className="truncate">{part.yard_name}</span>
          <span className="mx-1">·</span>
          <MapPin size={12} /> {part.city}
          {part.distance_km != null && <span className="mr-auto text-[var(--amber)] font-bold whitespace-nowrap">يبعد {part.distance_km} كم</span>}
        </div>
      </div>
    </Link>
  );
};

export const YardCard = ({ yard }) => (
  <Link to={`/yard/${yard.id}`} data-testid={`yard-card-${yard.id}`} className="ts-card p-5 flex items-center gap-4 animate-fade-up">
    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[var(--bg-secondary)]">
      {yard.logo ? <img src={fileUrl(yard.logo)} alt={yard.name} className="h-full w-full object-cover" /> :
        <div className="flex h-full w-full items-center justify-center font-head text-2xl font-black text-[var(--amber)]">{yard.name?.[0]}</div>}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <h3 className="font-bold text-white truncate">{yard.name}</h3>
        {yard.verified && <ShieldCheck size={16} className="text-[var(--emerald)] shrink-0" />}
      </div>
      <div className="flex items-center gap-2 mt-1 text-xs text-[var(--text-muted)]">
        <Stars value={yard.rating_avg} size={12} />
        <span>({yard.rating_count})</span>
        <span className="flex items-center gap-0.5"><MapPin size={12} /> {yard.city}</span>
      </div>
      {yard.parts_count != null && <p className="text-xs text-[var(--amber)] mt-1">{yard.parts_count} قطعة متوفرة{yard.distance_km != null ? ` · يبعد ${yard.distance_km} كم` : ""}</p>}
    </div>
  </Link>
);

export const Loader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--border-c)] border-t-[var(--amber)]" />
  </div>
);

export const Empty = ({ text = "لا توجد نتائج", action }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <p className="text-[var(--text-muted)] mb-4">{text}</p>
    {action}
  </div>
);
